/**
 * A voicing family describes which voices of a four-note close-position chord
 * are displaced down an octave. Voice numbers are counted from the top, which
 * is the convention used by the names: Drop 2 lowers the second voice from the
 * top; Drop 3 lowers the third.
 */
export const DROP_VOICING_FAMILY_IDS = ['drop-2', 'drop-3'] as const;
export type DropVoicingFamily = (typeof DROP_VOICING_FAMILY_IDS)[number];
export const VOICING_FAMILY_IDS = [...DROP_VOICING_FAMILY_IDS, 'joe-pass'] as const;

export type VoicingFamily = (typeof VOICING_FAMILY_IDS)[number];

export interface VoicingFamilyDefinition {
  id: VoicingFamily;
  label: string;
  shortDescription: string;
  droppedVoicesFromTop: readonly number[];
}

export const VOICING_FAMILIES: Record<VoicingFamily, VoicingFamilyDefinition> = {
  'drop-2': {
    id: 'drop-2',
    label: 'Drop 2',
    shortDescription: 'Open the upper voices on adjacent strings.',
    droppedVoicesFromTop: [2],
  },
  'drop-3': {
    id: 'drop-3',
    label: 'Drop 3',
    shortDescription: 'Separate the bass voice on a skipped-string layout.',
    droppedVoicesFromTop: [3],
  },
  'joe-pass': {
    id: 'joe-pass',
    label: 'Joe Pass',
    shortDescription: 'Voice-lead through the transcribed Joe Pass chord shapes.',
    droppedVoicesFromTop: [],
  },
};

export function isVoicingFamily(value: unknown): value is VoicingFamily {
  return typeof value === 'string' && VOICING_FAMILY_IDS.includes(value as VoicingFamily);
}

export function voicingFamilyLabel(family: VoicingFamily): string {
  return VOICING_FAMILIES[family].label;
}

/**
 * What the Family control OFFERS: each physical family, or the combined
 * selection that lets the optimiser choose per chord. A selection is a
 * SEARCH SPACE, not a drop pattern — every concrete grip still belongs to
 * exactly one physical family (`fingering.voicingFamily`), which is why this
 * lives beside the registry instead of inside it.
 */
export const VOICING_FAMILY_SELECTION_IDS = ['drop-2', 'drop-3', 'drop-2-3', 'joe-pass'] as const;

export type VoicingFamilySelection = (typeof VOICING_FAMILY_SELECTION_IDS)[number];

const SELECTION_LABELS: Record<VoicingFamilySelection, string> = {
  'drop-2': VOICING_FAMILIES['drop-2'].label,
  'drop-3': VOICING_FAMILIES['drop-3'].label,
  'drop-2-3': 'Drop 2 + 3',
  'joe-pass': VOICING_FAMILIES['joe-pass'].label,
};

const SELECTION_DESCRIPTIONS: Record<VoicingFamilySelection, string> = {
  'drop-2': VOICING_FAMILIES['drop-2'].shortDescription,
  'drop-3': VOICING_FAMILIES['drop-3'].shortDescription,
  'drop-2-3': 'Let the line pick per chord — whichever family moves less.',
  'joe-pass': VOICING_FAMILIES['joe-pass'].shortDescription,
};

export function isVoicingFamilySelection(value: unknown): value is VoicingFamilySelection {
  return (
    typeof value === 'string' &&
    VOICING_FAMILY_SELECTION_IDS.includes(value as VoicingFamilySelection)
  );
}

export function voicingFamilySelectionLabel(selection: VoicingFamilySelection): string {
  return SELECTION_LABELS[selection];
}

export function voicingFamilySelectionDescription(selection: VoicingFamilySelection): string {
  return SELECTION_DESCRIPTIONS[selection];
}

/** The physical families a selection searches, in preference order (drop-2
 *  first: ties in every engine break toward the earlier candidate). */
export function familiesOf(selection: VoicingFamilySelection): VoicingFamily[] {
  return selection === 'drop-2-3' ? ['drop-2', 'drop-3'] : [selection];
}

/** The concrete family a single-grip surface (finders, lab exhibits) uses
 *  when the session's selection is combined. */
export function primaryVoicingFamily(selection: VoicingFamilySelection): VoicingFamily {
  return familiesOf(selection)[0];
}

/**
 * Rotate the source close position, then move the voices named by the family
 * below the undropped voices. Each partition preserves its low-to-high order.
 * This exactly retains the original Drop 2 engine and also handles multiple
 * drops such as Drop 2 & 4 (the two lowered voices keep their relative order).
 */
export function createFamilyVoicing(
  notes: string[],
  position: number,
  family: VoicingFamily,
): string[] {
  // A collection of physical grips has no synthetic close-stack recipe.
  if (family === 'joe-pass') return [];
  if (notes.length === 0) return [];

  const close = [...notes];
  const turns = ((Math.trunc(position) % close.length) + close.length) % close.length;
  const rotated = [...close.slice(turns), ...close.slice(0, turns)];
  const dropped = VOICING_FAMILIES[family].droppedVoicesFromTop;
  const lowered = rotated.filter((_, index) => dropped.includes(rotated.length - index));
  const retained = rotated.filter((_, index) => !dropped.includes(rotated.length - index));
  return [...lowered, ...retained];
}
